/**
 * Manage Stream Key Managers Dialog
 *
 * Reason: Allows admins to assign managers to stream keys
 * for fine-grained access control (many-to-many relationship)
 */

import { FC, useState, useContext } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  CircularProgress,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  TextField,
  MenuItem,
  Typography,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQueryClient } from 'react-query';
import {
  useGetStreamKeyManagers,
  useAssignManagerToStreamKey,
  useRemoveManagerFromStreamKey,
  GET_STREAM_KEY_MANAGERS_CACHE_KEY,
} from '@/adapters/stream-keys';
import { useListOrgUsers } from '@/adapters/users-v2';
import { UserContext } from '@/contexts/UserContext';
import StreamKeyListItem from '@/entities/stream-key-list-item';

interface ManageStreamKeyManagersDialogProps {
  open: boolean;
  streamKey: StreamKeyListItem | null;
  onClose(): void;
}

/**
 * Dialog for managing stream key manager assignments
 *
 * @reason Provides UI for admins to assign and remove managers from stream keys
 */
const ManageStreamKeyManagersDialog: FC<ManageStreamKeyManagersDialogProps> = ({
  open,
  streamKey,
  onClose,
}) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { userState } = useContext(UserContext);
  const queryClient = useQueryClient();

  // Fetch stream key managers
  // Use streamKey.streamKey (the actual sk_... value) for API calls to eos-app
  const { data: managers = [], isLoading: loadingManagers } = useGetStreamKeyManagers(
    streamKey?.streamKey,
  );

  // Fetch all org users to select from (only managers, role=user)
  const { data: orgUsers = [], isLoading: loadingUsers } = useListOrgUsers(userState?.orgId);

  // Mutations
  const assignMutation = useAssignManagerToStreamKey(streamKey?.streamKey || '');
  const removeMutation = useRemoveManagerFromStreamKey(streamKey?.streamKey || '');

  /**
   * Filters users to show only managers (role=manager)
   * who are not already assigned to this stream key
   *
   * @reason Only managers can be assigned to stream keys
   */
  const availableManagers = orgUsers.filter((user) => {
    // Only show managers (role=manager)
    if (user.role !== 'manager') return false;

    // Exclude managers already assigned to this stream key
    const isAssigned = managers.some((manager) => manager.userId === user.id);
    return !isAssigned;
  });

  /**
   * Handles assigning a manager to the stream key
   *
   * @reason Creates many-to-many relationship between manager and stream key
   */
  const handleAssign = async (): Promise<void> => {
    setError(null);

    if (!selectedUserId) {
      setError('Please select a manager');
      return;
    }

    if (!streamKey) return;

    try {
      await assignMutation.mutateAsync({
        userId: selectedUserId,
      });

      // Invalidate cache to refresh manager list
      await queryClient.invalidateQueries([GET_STREAM_KEY_MANAGERS_CACHE_KEY, streamKey.streamKey]);

      // Reset selection
      setSelectedUserId('');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to assign manager';
      setError(errorMessage);
    }
  };

  /**
   * Handles removing a manager from the stream key
   *
   * @reason Removes many-to-many relationship
   */
  const handleRemove = async (userId: string): Promise<void> => {
    if (!streamKey) return;

    const confirmRemove = window.confirm(
      `Are you sure you want to remove this manager from the stream key?`,
    );

    if (!confirmRemove) return;

    setError(null);

    try {
      await removeMutation.mutateAsync(userId);

      // Invalidate cache to refresh manager list
      await queryClient.invalidateQueries([GET_STREAM_KEY_MANAGERS_CACHE_KEY, streamKey.streamKey]);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove manager';
      setError(errorMessage);
    }
  };

  /**
   * Handles dialog close
   *
   * @reason Cleans up state when dialog is closed
   */
  const handleClose = (): void => {
    setSelectedUserId('');
    setError(null);
    onClose();
  };

  /**
   * Gets user details by ID
   *
   * @reason Helper to display user info in manager list
   */
  const getUserDetails = (userId: string): import('@/adapters/users-v2').OrgUser | undefined => {
    return orgUsers.find((u) => u.id === userId);
  };

  const isLoading = assignMutation.isLoading || removeMutation.isLoading;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Manage Stream Key Managers</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Stream Key: <strong>{streamKey?.streamKey}</strong>
        </Typography>

        {/* Assign New Manager Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Assign Manager to Stream Key
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              select
              label="Select Manager"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              disabled={isLoading || loadingUsers}
              fullWidth
            >
              {availableManagers.length === 0 ? (
                <MenuItem disabled>No managers available</MenuItem>
              ) : (
                availableManagers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.email} {user.name ? `(${user.name})` : ''}
                  </MenuItem>
                ))
              )}
            </TextField>

            <Button
              variant="contained"
              onClick={handleAssign}
              disabled={!selectedUserId || isLoading || loadingUsers}
              sx={{ mt: 1 }}
            >
              Assign
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Current Managers Section */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          Current Managers ({managers.length})
        </Typography>

        {loadingManagers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : managers.length === 0 ? (
          <Typography color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
            No managers assigned to this stream key yet
          </Typography>
        ) : (
          <List>
            {managers.map((manager) => {
              const userDetails = getUserDetails(manager.userId);
              return (
                <ListItem
                  key={manager.id}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleRemove(manager.userId)}
                      disabled={isLoading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={
                      manager.name ||
                      manager.email ||
                      userDetails?.email ||
                      `User ID: ${manager.userId}`
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                        <Chip label="Manager" size="small" color="primary" />
                        {manager.email && (
                          <Typography variant="caption" color="textSecondary">
                            {manager.email}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageStreamKeyManagersDialog;
